import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Loader2, Search, Copy, UserCheck, Trash2, Sparkles, StickyNote } from "lucide-react";

const STAGES = ["prospect", "contacted", "onboarded", "declined"];
const STAGE_TINT = {
  prospect: "bg-slate-500/15 text-slate-300",
  contacted: "bg-blue-500/15 text-blue-300",
  onboarded: "bg-emerald-500/15 text-emerald-300",
  declined: "bg-red-500/15 text-red-300",
};

export const SalesRecruitment = ({ onOnboarded }) => {
  const [candidates, setCandidates] = useState([]);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.getPartnerCandidates().then(setCandidates);
  useEffect(() => { load(); }, []);

  const recruit = async () => {
    setBusy(true);
    try { const d = await api.recruitPartners(brief.trim()); setCandidates(d); toast.success(`KURT lined up ${d.length} sales-partner candidates.`); }
    catch { toast.error("Recruitment search failed."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="sales-recruitment" className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Briefcase size={18} className="text-blue-400" />
        <h2 className="kz-head text-lg font-bold">Recruit independent sales partners</h2>
      </div>
      <p className="text-sm text-slate-400 mb-3">KURT finds people with trust &amp; reach into UK SMEs and drafts each a recruitment pitch. Onboard one and they get a link that earns £10/mo per active member — recorded alongside member referrals.</p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input data-testid="recruit-brief-input" value={brief} onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && recruit()}
          placeholder="Who to recruit (e.g. 'accountants and networking hosts in the North West')…"
          className="flex-1 bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-blue-500/40 transition-all" />
        <button data-testid="recruit-btn" onClick={recruit} disabled={busy}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 text-white font-semibold text-sm hover:bg-blue-400 disabled:opacity-60 transition-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find partners
        </button>
      </div>

      {candidates.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-6 text-center text-sm text-slate-400">No candidates yet — let KURT hunt some.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {candidates.map((c) => <CandidateCard key={c.id} c={c} onChange={load} onOnboarded={onOnboarded} />)}
        </div>
      )}
    </div>
  );
};

const CandidateCard = ({ c, onChange, onOnboarded }) => {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(c.notes || "");
  const [notesOpen, setNotesOpen] = useState(false);
  const onboarded = c.status === "onboarded";

  const copy = () => { navigator.clipboard?.writeText(c.recruitment_message || ""); toast.success("Pitch copied."); };
  const onboard = async () => {
    const name = window.prompt("Onboard this partner — their name:", c.persona?.split(",")[0] || c.candidate_type || "");
    if (!name) { setBusy(false); return; }
    setBusy(true);
    try { await api.onboardPartner(c.id, name.trim(), ""); await onChange(); onOnboarded && onOnboarded(); toast.success(`${name.trim()} onboarded as a sales partner.`); }
    catch { toast.error("Onboard failed."); }
    finally { setBusy(false); }
  };
  const setStage = async (status) => {
    if (status === "onboarded") { await onboard(); return; }
    setBusy(true);
    try { await api.patchCandidate(c.id, { status }); await onChange(); } finally { setBusy(false); }
  };
  const saveNotes = async () => { setBusy(true); try { await api.patchCandidate(c.id, { notes }); await onChange(); toast.success("Notes saved."); } finally { setBusy(false); } };
  const remove = async () => { setBusy(true); try { await api.deleteCandidate(c.id); await onChange(); } finally { setBusy(false); } };

  return (
    <div data-testid={`candidate-${c.id}`} className="rounded-2xl border border-white/10 bg-[#121824] p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">{c.candidate_type}</span>
            <span data-testid={`status-${c.id}`} className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${STAGE_TINT[c.status] || STAGE_TINT.prospect}`}>{c.status}</span>
          </div>
          <p className="kz-head font-bold text-slate-100 mt-0.5 leading-snug">{c.persona}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="kz-head text-lg font-extrabold text-blue-400">{c.fit_score}</div>
          <div className="text-[9px] text-slate-500 uppercase">fit</div>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{c.why_them}</p>
      <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
        {c.estimated_reach ? <span className="px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">~{c.estimated_reach} SMEs reach</span> : null}
        {c.earning_pitch && <span className="text-emerald-400/90">{c.earning_pitch}</span>}
        {c.contacted_at && <span className="text-slate-500">contacted {new Date(c.contacted_at).toLocaleDateString()}</span>}
      </div>
      {c.recruitment_message && (
        <div className="mt-2 rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
          <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-4 whitespace-pre-wrap">{c.recruitment_message}</p>
        </div>
      )}

      <div className="flex items-center gap-1 mt-3">
        {STAGES.map((s) => (
          <button key={s} data-testid={`stage-${s}-${c.id}`} onClick={() => setStage(s)} disabled={busy || c.status === s}
            className={`flex-1 text-[9px] font-bold uppercase py-1 rounded-md transition-all disabled:opacity-100 ${c.status === s ? STAGE_TINT[s] : "bg-white/[0.04] text-slate-500 hover:bg-white/[0.08]"}`}>
            {s === "onboarded" ? "onboard" : s}
          </button>
        ))}
      </div>

      {notesOpen && (
        <div className="mt-2">
          <textarea data-testid={`notes-${c.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes about this partner…"
            className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 outline-none focus:border-blue-500/40 resize-y" />
          <button data-testid={`save-notes-${c.id}`} onClick={saveNotes} disabled={busy} className="mt-1 text-[11px] font-semibold text-blue-300 hover:text-blue-200">Save notes</button>
        </div>
      )}

      <div className="flex gap-1.5 mt-3">
        <button data-testid={`copy-pitch-${c.id}`} onClick={copy} className="flex items-center justify-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all"><Copy size={12} /> Pitch</button>
        <button data-testid={`notes-toggle-${c.id}`} onClick={() => setNotesOpen(!notesOpen)} className="flex items-center justify-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all"><StickyNote size={12} /> Notes{c.notes ? " •" : ""}</button>
        {onboarded && <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-400 px-2"><UserCheck size={13} /> Partner</span>}
        <button data-testid={`del-candidate-${c.id}`} onClick={remove} disabled={busy} className="flex items-center justify-center px-2 rounded-lg border border-red-500/25 text-red-300 hover:bg-red-500/10 transition-all ml-auto"><Trash2 size={12} /></button>
      </div>
    </div>
  );
};
